import {
  TierInfo,
  UserTierTypes,
} from '@eCommerce/frontend-legacy-shared-library-native';

/**
 * Local copy of the shared library's getTierProgress. Keep in sync until the
 * library version carrying it is published and pinned here.
 */

export interface GetTierProgressArgs {
  tierCredits: number;
  currentLevel?: UserTierTypes;
  currentTierInfo?: TierInfo;
  nextTierInfo?: TierInfo;
  allTiers?: TierInfo[];
}

export interface TierProgress {
  /** Fill from 0 to 1, measured from 0 credits to the target minimum. */
  percentage: number;
  remainingCredits: number;
  targetName: string;
  byInvitation: boolean;
  hideLabel: boolean;
  invitationRequired: boolean;
}

/**
 * Only consulted when a tier omits isEarnable. Olympus is the one tier no
 * balance can reach; Diamond Plus and Diamond Elite are earned on credits.
 */
const NON_EARNABLE_DEFAULTS = new Set<string>([UserTierTypes.OLYMPUS]);

const INVITATION_DEFAULTS = new Set<string>([
  UserTierTypes.SEVEN_STARS,
  UserTierTypes.OLYMPUS,
]);

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const emptyProgress = (): TierProgress => ({
  percentage: 0,
  remainingCredits: 0,
  targetName: '',
  byInvitation: false,
  hideLabel: false,
  invitationRequired: false,
});

export const isEarnableTier = (tier: TierInfo): boolean => {
  if (typeof tier.isEarnable === 'boolean') {
    return tier.isEarnable;
  }
  return !NON_EARNABLE_DEFAULTS.has(tier.guestprofiletiercode);
};

export const isInvitationTier = (tier: TierInfo): boolean => {
  if (typeof tier.byInvitation === 'boolean') {
    return tier.byInvitation;
  }
  return INVITATION_DEFAULTS.has(tier.guestprofiletiercode);
};

/**
 * Progress always runs from 0 credits to the minimum of the next earnable tier
 * (Olympus is skipped). The displayed tier is only used to
 * decide whether a maxed-out balance reads "Invitation Required" or nothing,
 * so a Diamond whose credits reset on 1/1 progresses toward Platinum again.
 */
export const getTierProgress = ({
  tierCredits,
  currentLevel,
  currentTierInfo,
  nextTierInfo,
  allTiers,
}: GetTierProgressArgs): TierProgress => {
  if (!currentLevel || !currentTierInfo) {
    return emptyProgress();
  }

  // Never name Olympus, even for a member who holds it: the tier is
  // unannounced, so no caller should be able to render it from this result.
  if (currentLevel === UserTierTypes.OLYMPUS) {
    return {...emptyProgress(), percentage: 1, hideLabel: true};
  }

  const ladder =
    allTiers && allTiers.length > 0
      ? allTiers
      : ([currentTierInfo, nextTierInfo].filter(Boolean) as TierInfo[]);

  const credits = Math.max(0, tierCredits);
  const earnable = ladder
    .filter(isEarnableTier)
    .slice()
    .sort((a, b) => a.mincredits - b.mincredits);

  const maxEarnable = earnable[earnable.length - 1];
  const nextEarnable = earnable.find(tier => credits < (tier.mincredits || 0));

  if (!nextEarnable) {
    const notYetEarnedMax =
      !!maxEarnable &&
      (currentTierInfo.mincredits || 0) < (maxEarnable.mincredits || 0);
    return {
      ...emptyProgress(),
      percentage: 1,
      hideLabel: !notYetEarnedMax,
      invitationRequired: notYetEarnedMax,
    };
  }

  const target = nextEarnable.mincredits || 0;
  return {
    ...emptyProgress(),
    percentage: target > 0 ? clamp01(credits / target) : 0,
    remainingCredits: Math.max(0, target - credits),
    targetName: nextEarnable.name || '',
    byInvitation: isInvitationTier(nextEarnable),
  };
};
